import { Navigate, useLocation } from 'react-router-dom'
import { useCurrentUser } from '../queries/useCurrentUser'
import { useSelector } from 'react-redux'
import { Indicators } from 'ui'
import { IStoreState } from '../store'

export const RequireOnboarding: React.FC<{ children: JSX.Element }> = ({
  children,
}) => {
  try {
    const location = useLocation()
    const { data: user, isLoading } = useCurrentUser()

    const isAuthenticated = useSelector<IStoreState, boolean>((state) => {
      try {
        if (!state || !state.session) {
          console.error(
            'RequireOnboarding: Missing state or session in isAuthenticated check'
          )
          return false
        }
        return state.session.isAuthenticated
      } catch (error) {
        console.error(
          'RequireOnboarding: Error in isAuthenticated check:',
          error
        )
        return false
      }
    })

    const onboardingRequired = useSelector<IStoreState, boolean>((state) => {
      try {
        if (!state || !state.session) {
          console.error(
            'RequireOnboarding: Missing state or session in onboardingRequired check'
          )
          return false
        }
        return state.session.onboardingRequired
      } catch (error) {
        console.error(
          'RequireOnboarding: Error in onboardingRequired check:',
          error
        )
        return false
      }
    })

    const skippedOnboarding = useSelector<IStoreState, boolean>((state) => {
      try {
        if (!state || !state.session) {
          console.error(
            'RequireOnboarding: Missing state or session in skippedOnboarding check'
          )
          return false
        }
        return state.session.skippedOnboarding
      } catch (error) {
        console.error(
          'RequireOnboarding: Error in skippedOnboarding check:',
          error
        )
        return false
      }
    })

    // Show loading indicator while fetching user data
    if (isLoading) {
      console.log(
        'RequireOnboarding: Still loading user data, showing loading screen'
      )
      return <Indicators.FullScreen />
    }

    // If user is not authenticated, don't try to redirect to onboarding
    // Or if onboarding is not required OR user has explicitly skipped it
    if (!isAuthenticated || !onboardingRequired || skippedOnboarding) {
      console.log(
        'RequireOnboarding: Onboarding not needed, continuing to children'
      )
      return children
    }

    // Double-check if user has keywords in profile
    const hasKeywords = user?.keywords && user.keywords.length > 0

    if (!hasKeywords) {
      // Determine redirect based on user role
      const redirectPath =
        user?.userRole === 'EDUCATOR'
          ? '/onboard/educator/start'
          : '/onboard/professional/start'

      // Only redirect if not already on the onboarding path
      if (!location.pathname.startsWith('/onboard')) {
        console.log(
          'RequireOnboarding: Redirecting to onboarding path:',
          redirectPath
        )
        return <Navigate to={redirectPath} replace />
      }
    }

    console.log('RequireOnboarding: All checks passed, continuing to children')
    return children
  } catch (error) {
    console.error('RequireOnboarding: Unexpected error:', error)
    // If there's an error, just continue to the children as a fallback
    return children
  }
}

// This is replaced with dispatch(setSkippedOnboarding(true)) in individual components
// No longer export this function
